// Per-visitor page-visit timeline. Seeded from the DB via REST when a
// conversation opens (persistent history), then kept live by `visitor:page`
// bus events appended in real time. Entries are held most-recent-first to
// match how the timeline renders and how the server returns them.

import { create } from 'zustand';
import type { VisitorId } from '@prakash-software/shared';

export interface PageVisit {
  url: string;
  at: number;
}

interface PageVisitsState {
  byVisitor: Map<VisitorId, PageVisit[]>;
  /** Replace the timeline for a visitor (from the REST fetch). */
  setAll: (id: VisitorId, visits: PageVisit[]) => void;
  /** Append a live visit, newest-first, deduping a repeat of the latest URL. */
  append: (id: VisitorId, visit: PageVisit) => void;
}

export const usePageVisitsStore = create<PageVisitsState>((set) => ({
  byVisitor: new Map(),
  setAll: (id, visits) =>
    set((s) => {
      const next = new Map(s.byVisitor);
      next.set(id, visits);
      return { byVisitor: next };
    }),
  append: (id, visit) =>
    set((s) => {
      const next = new Map(s.byVisitor);
      const existing = next.get(id) ?? [];
      // Newest-first: a repeat of the current top URL is a no-op.
      if (existing[0]?.url === visit.url) return {};
      next.set(id, [visit, ...existing]);
      return { byVisitor: next };
    }),
}));
